import { Icon, type IconName } from '@mdvip/shared/components/blocks/Icon';
import { useCurrency } from '@mdvip/shared/hooks/useCurrency';
import { cn } from '@mdvip/shared/lib/utils';
import type { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { BillingAddressValues, PaymentAccountFormValues } from '@/lib/payments/paymentAccount.types';

// Receipt-style label · value line (mirrors PaymentReviewScreen's private ReviewLine).
function ReviewLine({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-right text-sm font-medium text-foreground">{value}</dd>
    </div>
  );
}

interface EditReviewScreenProps {
  /** Which form slice to read HSA/Default from — `values.card` or `values.bank`. */
  scope: 'card' | 'bank';
  icon: IconName;
  /** e.g. "VISA ···· 1111" for a card, "Chase ···· 6789" for a bank account. */
  headline: string;
  /** e.g. "Exp 07/29" for a card, "Checking" for a bank account. */
  caption: string;
  /** Real member-level AutoPay enrollment — Edit never writes this, only displays it. */
  autoPayEnrolled: boolean;
  autoPayAmount: number | null;
  autoPayPlan: string | null;
  billing: BillingAddressValues;
}

/**
 * Screen 2 of the Edit Payment Account modal — same receipt layout for both credit card and bank
 * account. The identity header (icon/headline/caption) is computed by the caller from its own
 * immutable account fields (network/last4, or bank name/last6) since those never live on the form;
 * HSA/Default come from the live form's `scope` slice, and AutoPay is the member's real status.
 * The orchestrator owns the footer actions (Make changes / Confirm).
 */
export function EditReviewScreen({
  scope,
  icon,
  headline,
  caption,
  autoPayEnrolled,
  autoPayAmount,
  autoPayPlan,
  billing,
}: EditReviewScreenProps) {
  const { t } = useTranslation('members');
  const { formatCurrency } = useCurrency();
  const { getValues } = useFormContext<PaymentAccountFormValues>();
  const values = getValues()[scope];

  const yn = (flag: boolean) =>
    flag ? t('payments.addAccount.reviewScreen.yes') : t('payments.addAccount.reviewScreen.no');

  const hasBilling = billing.name.trim() !== '';

  return (
    <div className="mx-auto max-w-md overflow-hidden rounded-2xl border border-border bg-card">
      {/* Receipt header */}
      <div className="flex flex-col items-center border-b border-dashed border-border px-6 pb-6 pt-7 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-tint-brand text-primary">
          <Icon name={icon} size="xl" />
        </span>
        <div className="mt-3 text-sm text-muted-foreground">{t('payments.editAccount.reviewScreen.youEditing')}</div>
        <div className="mt-1 text-2xl font-extrabold tracking-tight text-foreground">{headline}</div>
        <div className="mt-1 text-sm text-muted-foreground">
          {caption}
          {values.makeDefault ? ` · ${t('payments.addAccount.reviewScreen.defaultMethodSuffix')}` : ''}
        </div>
      </div>

      <div className="px-6 py-5">
        {autoPayEnrolled && (
          <div className="rounded-2xl bg-muted p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-foreground">
                {t('payments.addAccount.reviewScreen.recurringAutoPay')}
              </span>
              <span className="text-2xl font-extrabold tabular-nums text-primary">
                {autoPayAmount === null ? '—' : formatCurrency(autoPayAmount)}
              </span>
            </div>
            <div className="mt-1 text-xs text-muted-foreground">{autoPayPlan ?? '—'}</div>
          </div>
        )}

        <dl className={cn('divide-y divide-border/70', autoPayEnrolled && 'mt-4')}>
          <ReviewLine label={t('payments.addAccount.reviewScreen.hsa')} value={yn(values.isHsa)} />
          {!autoPayEnrolled && (
            <ReviewLine
              label={t('payments.addAccount.reviewScreen.autoPay')}
              value={t('payments.addAccount.reviewScreen.off')}
            />
          )}
          <ReviewLine label={t('payments.addAccount.reviewScreen.setAsDefault')} value={yn(values.makeDefault)} />
          <ReviewLine
            label={t('payments.addAccount.reviewScreen.billing')}
            value={
              hasBilling ? (
                <span className="text-right leading-relaxed">
                  {billing.name}
                  <br />
                  {billing.line1}
                  {billing.line2 ? `, ${billing.line2}` : ''}
                  <br />
                  {billing.city ? `${billing.city}, ` : ''}
                  {billing.state} {billing.zip}
                </span>
              ) : (
                t('payments.addAccount.reviewScreen.billingNone')
              )
            }
          />
        </dl>
      </div>
    </div>
  );
}
